import { computed, type ComputedRef, type Ref } from 'vue'
import type {
    CustomerRegistrationInput,
    ProductAnalyticsFragment,
    RingBuilderResolveExistingCustomerMutation,
    RingBuilderResolveNewCustomerMutation,
    RingSizeInput,
} from '@/graphql/api'
import { RingBuilderResolveMode } from '@/graphql/api'

import { createResponseAndPath, type ResponseAndPath } from '@/helpers/errors'
import { useHandleAddToCartSuccess } from '../../composables/useHandleAddToCartSuccess'
import { useEngagementRingGetInput } from './useEngagementRingGetInput'
import { useRegionPlugin } from '@/plugins/region'
import { useTranslator } from '@/i18n'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRingBuilderResolveExistingCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveExistingCustomer.mutation.generated.ts'
import { useRingBuilderResolveNewCustomerMutation } from '@/graphql/api/mutations/ringBuilderResolveNewCustomer.mutation.generated.ts'
import type { RingBuilder } from '@/plugins/ring-builder.ts'

export interface EngagementRingAddToCart {
    addToCartNewCustomer: (successCallback: () => void) => void
    addToCartErrors: readonly [
        ResponseAndPath<RingBuilderResolveExistingCustomerMutation>,
        ResponseAndPath<RingBuilderResolveNewCustomerMutation>,
    ]
    addToCartExistingCustomer(): void
    fetchingAddToCart: ComputedRef<boolean>
}

export const useEngagementRingAddToCart = (
    ring: Readonly<Ref<ProductAnalyticsFragment | undefined | null>>,
    ringBuilder: Readonly<Ref<RingBuilder | undefined>>,
    centreStoneUri: Readonly<Ref<string | null>>,
    engraving: Readonly<Ref<string>>,
    customerRegistrationInput: Readonly<
        Ref<CustomerRegistrationInput | undefined>
    >,
    handleRingSizeNotSelected: () => RingSizeInput | null
): EngagementRingAddToCart => {
    const currency = useCurrencyInput()
    const { regionCode } = useRegionPlugin()
    const { locale } = useTranslator()

    const isAdmin = useIsAdmin()

    const { getInput } = useEngagementRingGetInput(
        centreStoneUri,
        engraving,
        handleRingSizeNotSelected
    )

    const { prepare } = useHandleAddToCartSuccess(ring, ringBuilder)

    // Existing customer
    const responseExistingCustomer =
        useRingBuilderResolveExistingCustomerMutation()

    const {
        executeMutation: mutationExistingCustomer,
        fetching: fetchingExistingCustomer,
    } = responseExistingCustomer

    const addToCartExistingCustomer = () => {
        const input = getInput(RingBuilderResolveMode.AddToCart)

        if (!input) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationExistingCustomer({
            input,
            locale: locale.value,
            region: regionCode.value,
            currency: currency.value,
        }).then(({ error, data: response }) => {
            const data = response?.ringBuilderResolveExistingCustomer

            if (data?.__typename !== 'RingBuilderResolvedFailure') {
                handleAddToCartSuccess({
                    error,
                    data: {
                        customer: undefined,
                    },
                })
            }
        })
    }

    // New customer
    const responseNewCustomer = useRingBuilderResolveNewCustomerMutation()

    const {
        executeMutation: mutationNewCustomer,
        fetching: fetchingNewCustomer,
    } = responseNewCustomer

    const addToCartNewCustomer = (successCallback: () => void) => {
        const input = getInput(RingBuilderResolveMode.AddToCart)
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationNewCustomer({
            input,
            customer,
            locale: locale.value,
            region: regionCode.value,
            currency: currency.value,
            isConsultant: isAdmin.value,
        }).then(({ error, data: response }) => {
            const data = response?.ringBuilderResolveNewCustomer

            if (data?.__typename !== 'RingBuilderResolvedFailure') {
                handleAddToCartSuccess({
                    error,
                    data,
                })

                if (data?.customer.login) {
                    successCallback()
                }
            }
        })
    }

    const addToCartErrors = [
        createResponseAndPath(
            responseExistingCustomer,
            'ringBuilderResolveExistingCustomer'
        ),
        createResponseAndPath(
            responseNewCustomer,
            'ringBuilderResolveNewCustomer'
        ),
    ] as const

    const fetchingAddToCart = computed(
        () => fetchingExistingCustomer.value || fetchingNewCustomer.value
    )

    return {
        addToCartNewCustomer,
        addToCartErrors,
        addToCartExistingCustomer,
        fetchingAddToCart,
    }
}
